import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { isTrialing, isExpired } from "@/lib/plans";

/**
 * Shows trial countdown banner at the top of the app shell. Hides if user is
 * admin or already on a paid plan.
 */
export default function TrialBanner() {
  const { user } = useAuth();
  if (!user || user.is_admin) return null;
  const state = user.plan_state || {};
  const trialing = isTrialing(user);
  const expired = isExpired(user);

  if (!trialing && !expired) return null;

  if (expired) {
    return (
      <div
        data-testid="trial-expired-banner"
        className="bg-red-500/15 border-b border-red-500/40 px-4 py-3 flex items-center justify-between gap-4 flex-wrap"
      >
        <div className="flex items-center gap-3 text-sm">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-red-100">
            <strong>Seu trial expirou.</strong> Escolha um plano para continuar usando o SmartStockAI.
          </span>
        </div>
        <Link to="/app/pricing">
          <Button data-testid="trial-expired-cta" size="sm" className="bg-primary hover:bg-primary/90">
            Escolher plano
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    );
  }

  const days = state.trial_days_left ?? 14;
  const urgent = days <= 3;

  return (
    <div
      data-testid="trial-banner"
      className={`px-4 py-2.5 flex items-center justify-between gap-4 flex-wrap border-b ${
        urgent
          ? "bg-red-500/10 border-red-500/30"
          : "bg-primary/10 border-primary/30"
      }`}
    >
      <div className="flex items-center gap-2.5 text-sm">
        <Sparkles className={`w-4 h-4 shrink-0 ${urgent ? "text-red-400" : "text-primary"}`} />
        <span>
          <strong>Trial gratuito:</strong>{" "}
          {days > 0
            ? `${days} ${days === 1 ? "dia restante" : "dias restantes"} de acesso completo.`
            : "Último dia."}
        </span>
      </div>
      <Link to="/app/pricing">
        <Button data-testid="trial-banner-cta" size="sm" variant="outline" className="border-primary/40 text-primary hover:bg-primary/10 h-8">
          Ver planos
          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </Link>
    </div>
  );
}
